import * as React from "react";
import { useEffect, useMemo, useState, useCallback } from "react";
import styled, { css } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import Routes from "routing/Routes";
import { Link, Select } from "components/common";
import { Alert, Button, Row, Col, Input } from "components/bootstrap";
import useInputSetupWizard from "components/inputs/InputSetupWizard/hooks/useInputSetupWizard";
import useInputSetupWizardSteps from "components/inputs/InputSetupWizard/hooks/useInputSetupWizardSteps";
import useInputSetupWizardStepsHelper from "components/inputs/InputSetupWizard/hooks/useInputSetupWizardStepsHelper";
import { defaultCompare } from "logic/DefaultCompare";
import { INPUT_WIZARD_STEPS } from "components/inputs/InputSetupWizard/types";
import CreateStreamForm from "components/inputs/InputSetupWizard/steps/components/CreateStreamForm";
import useFilteredStreams from "components/inputs/InputSetupWizard/hooks/useFilteredStreams";
import usePipelinesConnectedStream from "hooks/usePipelinesConnectedStream";
import {
  StepWrapper,
  DescriptionCol,
  ButtonCol,
  StyledHeading,
  StyledList,
  RecommendedTooltip
} from "./components/StepWrapper";
import InputInUseAlert from "./components/InputInUseAlert";
const ExistingStreamCol = styled(Col)(
  ({ theme }) => css`
    padding-top: ${theme.spacings.sm};
    padding-bottom: ${theme.spacings.md};
  `
);
const CreateStreamCol = styled(Col)(
  ({ theme }) => css`
    padding-top: ${theme.spacings.sm};
    padding-bottom: ${theme.spacings.md};
    border-right: 1px solid ${theme.colors.cards.border};
  `
);
const StyledLabel = styled.label(
  ({ theme }) => css`
    font-weight: normal;
    line-height: 1.1;
    margin-bottom: ${theme.spacings.sm};
    display: inline-block;
    font-size: ${theme.fonts.size.h3};
    background: none;
  `
);
const StyledAlert = styled(Alert)`
  margin-top: 0;
`;
const SetupRoutingStep = () => {
  var _a, _b, _c;
  const { t } = useTranslation("inputs");
  const [showSelectStream, setShowSelectStream] = useState(false);
  const [showCreateStream, setShowCreateStream] = useState(false);
  const currentStepName = useMemo(() => INPUT_WIZARD_STEPS.SETUP_ROUTING, []);
  const { goToPreviousStep, goToNextStep, orderedSteps, activeStep, wizardData } = useInputSetupWizard();
  const { stepsData, setStepsData } = useInputSetupWizardSteps();
  const { checkHasPreviousStep, checkHasNextStep, updateStepData, getStepData } = useInputSetupWizardStepsHelper();
  const newStream = getStepData(stepsData, currentStepName, "newStream");
  const selectedStreamId = getStepData(stepsData, currentStepName, "streamId");
  const isDefaultStream = getStepData(stepsData, currentStepName, "streamType") === "DEFAULT";
  const removeFromDefault = getStepData(stepsData, currentStepName, "removeMatchesFromDefault");
  const removeFromDefaultStreamChecked = typeof removeFromDefault === "undefined" ? true : removeFromDefault;
  const hasPreviousStep = checkHasPreviousStep(orderedSteps, activeStep);
  const hasNextStep = checkHasNextStep(orderedSteps, activeStep);
  const { data: streamsData, isLoading: isLoadingStreams } = useFilteredStreams();
  const streams = streamsData == null ? void 0 : streamsData.streams;
  const { data: streamPipelinesData } = usePipelinesConnectedStream(selectedStreamId, !!selectedStreamId);
  const defaultStepData = { streamType: "DEFAULT" };
  const isStepValid = useCallback(() => {
    const stepData = getStepData(stepsData, currentStepName);
    if (!stepData) return false;
    const { streamType, streamId } = stepData;
    if (showCreateStream && !newStream) return false;
    switch (streamType) {
      case "NEW":
        if (!newStream) return false;
        return true;
      case "EXISTING":
        if (!streamId) return false;
        return true;
      case "DEFAULT":
        return true;
      default:
        return false;
    }
  }, [currentStepName, newStream, showCreateStream, stepsData, getStepData]);
  useEffect(() => {
    var _a2;
    if (orderedSteps && activeStep && stepsData) {
      if (!((_a2 = getStepData(stepsData, currentStepName)) == null ? void 0 : _a2.streamType)) {
        const withInitialStepsData = updateStepData(stepsData, currentStepName, defaultStepData);
        setStepsData(withInitialStepsData);
      }
    }
  }, []);
  const options = useMemo(() => {
    if (!streams) return [];
    return streams.filter(({ is_default, is_editable }) => !is_default && is_editable).sort(({ title: key1 }, { title: key2 }) => defaultCompare(key1, key2)).map(({ title, id }) => ({ label: title, value: id }));
  }, [streams]);
  const handleStreamSelect = (streamId) => {
    if (streamId) {
      setStepsData(
        updateStepData(stepsData, currentStepName, {
          streamId,
          streamType: "EXISTING",
          removeMatchesFromDefault: removeFromDefaultStreamChecked
        })
      );
    } else {
      setStepsData(
        updateStepData(stepsData, currentStepName, {
          streamId: void 0,
          streamType: "DEFAULT",
          removeMatchesFromDefault: void 0
        })
      );
    }
  };
  const handleCreateStream = () => {
    updateStepData(stepsData, currentStepName, defaultStepData);
    setShowCreateStream(true);
  };
  const handleSelectStream = () => {
    setShowSelectStream(true);
  };
  const onNextStep = () => {
    goToNextStep();
  };
  const handleBackClick = () => {
    setStepsData(updateStepData(stepsData, currentStepName, defaultStepData, true));
    setShowSelectStream(false);
    setShowCreateStream(false);
    goToPreviousStep();
  };
  const handleCheckRemoveFromDefaultStream = () => {
    setStepsData(
      updateStepData(stepsData, currentStepName, {
        removeMatchesFromDefault: !removeFromDefaultStreamChecked
      })
    );
  };
  const streamHasConnectedPipelines = streamPipelinesData && (streamPipelinesData == null ? void 0 : streamPipelinesData.length) > 0;
  const submitStreamCreation = ({
    create_new_pipeline,
    ...stream
  }) => {
    setStepsData(
      updateStepData(stepsData, currentStepName, {
        newStream: stream,
        shouldCreateNewPipeline: create_new_pipeline != null ? create_new_pipeline : false,
        streamType: "NEW"
      })
    );
    onNextStep();
  };
  const showNewStreamSection = newStream || showCreateStream;
  const showSelectStreamSection = selectedStreamId || showSelectStream;
  return /* @__PURE__ */ React.createElement(StepWrapper, null, /* @__PURE__ */ React.createElement(InputInUseAlert, { inputId: (_a = wizardData == null ? void 0 : wizardData.input) == null ? void 0 : _a.id }), selectedStreamId && streamHasConnectedPipelines && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning", title: t("setup_wizard.routing.connected_pipelines_title") }, t("setup_wizard.routing.connected_pipelines"), /* @__PURE__ */ React.createElement(StyledList, null, streamPipelinesData.map((pipeline) => /* @__PURE__ */ React.createElement("li", { key: pipeline.title }, /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.PIPELINES.PIPELINE(pipeline.id), target: "_blank" }, pipeline.title))))))), !showNewStreamSection && !showSelectStreamSection && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(DescriptionCol, { md: 12 }, /* @__PURE__ */ React.createElement(StyledList, null, /* @__PURE__ */ React.createElement("li", null, t("setup_wizard.routing.select_destination")), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "setup_wizard.routing.recommend_new_stream",
      components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
    }
  )), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "setup_wizard.routing.default_stream_fallback",
      components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
    }
  )), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "setup_wizard.routing.pipeline_rules_info", components: { strong: /* @__PURE__ */ React.createElement("strong", null) } }))))), /* @__PURE__ */ React.createElement(Row, null, !selectedStreamId && /* @__PURE__ */ React.createElement(CreateStreamCol, { md: 6 }, /* @__PURE__ */ React.createElement(StyledHeading, null, t("setup_wizard.routing.new_stream_heading")), /* @__PURE__ */ React.createElement(RecommendedTooltip, { opened: true, withArrow: true, position: "bottom", label: t("setup_wizard.routing.recommended") }, /* @__PURE__ */ React.createElement(Button, { onClick: handleCreateStream, bsStyle: "primary" }, t("setup_wizard.routing.create_stream")))), /* @__PURE__ */ React.createElement(ExistingStreamCol, { md: selectedStreamId ? 12 : 6 }, /* @__PURE__ */ React.createElement(StyledHeading, null, t("setup_wizard.routing.existing_stream_heading")), /* @__PURE__ */ React.createElement(Button, { onClick: handleSelectStream }, t("setup_wizard.routing.select_stream"))))), showNewStreamSection && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(StyledHeading, null, t("setup_wizard.routing.create_new_stream")), /* @__PURE__ */ React.createElement(
    CreateStreamForm,
    {
      submitForm: submitStreamCreation,
      handleBackClick,
      prevCreatedStream: (_b = stepsData == null ? void 0 : stepsData.SETUP_ROUTING) == null ? void 0 : _b.newStream,
      prevShouldCreateNewPipeline: (_c = stepsData == null ? void 0 : stepsData.SETUP_ROUTING) == null ? void 0 : _c.shouldCreateNewPipeline
    }
  ))), showSelectStreamSection && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(DescriptionCol, { md: 12 }, /* @__PURE__ */ React.createElement(StyledLabel, { htmlFor: "streams" }, t("setup_wizard.routing.choose_existing_stream")), /* @__PURE__ */ React.createElement(StyledList, null, /* @__PURE__ */ React.createElement("li", null, t("setup_wizard.routing.existing_stream_selected")), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "setup_wizard.routing.pipeline_rules_created",
      components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
    }
  ))))), !isLoadingStreams && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: "streams",
      onChange: handleStreamSelect,
      options,
      "aria-label": t("setup_wizard.routing.default_stream"),
      clearable: true,
      placeholder: t("setup_wizard.routing.default_stream"),
      value: selectedStreamId != null ? selectedStreamId : getStepData(stepsData, currentStepName, "streamId")
    }
  ), !isDefaultStream && /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "existing_remove_matches_from_default_stream",
      type: "checkbox",
      label: t("setup_wizard.routing.remove_from_default"),
      title: t("setup_wizard.routing.remove_from_default"),
      help: /* @__PURE__ */ React.createElement("span", null, t("setup_wizard.routing.remove_from_default_help")),
      name: "existing_remove_matches_from_default_stream",
      checked: removeFromDefaultStreamChecked,
      onChange: handleCheckRemoveFromDefaultStream
    }
  )))), ((hasPreviousStep || hasNextStep) && !showNewStreamSection || showSelectStreamSection) && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(ButtonCol, { md: 12 }, (hasPreviousStep || showSelectStreamSection) && /* @__PURE__ */ React.createElement(Button, { onClick: handleBackClick }, t("setup_wizard.back")), hasNextStep && /* @__PURE__ */ React.createElement(Button, { disabled: !isStepValid(), onClick: onNextStep, bsStyle: "primary" }, t("common:actions.next")))));
};
export default SetupRoutingStep;
